import * as R from 'ramda';
import styled from 'styled-components';
import {
  space,
  width,
  color,
  zIndex,
  height,
  border,
  fontSize,
  minWidth,
  minHeight,
  maxHeight,
  borderRadius,
} from 'styled-system';
// helpers/constants
import * as G from '../../helpers';
// ui
import { Box, Flex, RelativeFlex } from '../../ui';
//////////////////////////////////////////////////

export const DisableRelativeFlex = styled(RelativeFlex)`
  ${({ disabled }) => (
    R.and(
      disabled,
      `
        opacity: 0.5;
        pointer-events: none;
      `,
    )
  )}
`;

export const EmptyList = styled.div`
  font-size: 36px;
  padding: ${({ p }) => p || '50px 0'};
  color: ${() => G.getTheme('colors.light.lightGrey')};
  text-align: ${({ textAlign }) => textAlign || 'center'};
  background-color: ${() => G.getTheme('colors.light.mainLight')};
`;

export const EmptyTableList = styled(EmptyList)`
  left: 0;
  width: 100%;
  position: sticky;
  box-sizing: border-box;
`;

export const TableWrapper = styled.div`
  ${width}
  ${space}
  ${color}
  ${border}
  ${height}
  ${minHeight}
  ${maxHeight}

  overflow: ${({ overflow }) => overflow || 'auto'};
  border: ${({ withBorder }) => withBorder && `1px solid ${G.getTheme('tables.rows.borderColor')}`};
`;

TableWrapper.defaultProps = {
  pb: '1px',
  maxHeight: 500,
};

export const TableInner = styled.div`
  ${width}
  ${minWidth}
`;

TableInner.defaultProps = {
  minWidth: 'fit-content',
};

export const getTitleRowBgColor = (props) => G.ifElse(
  G.isTrue(props.colored),
  G.getTheme(G.ifElse(props.mainColor, 'tables.rows.additionalBgColor', 'tables.rows.bgColorActive')),
  R.or(props.bgColor, G.getTheme('tables.header.bgColor')),
);

export const TitleRow = styled.div`
  ${space}
  ${color}
  ${height}
  ${fontSize}

  display: flex;
  min-width: 100%;
  width: fit-content;

  background-color: ${(props) => getTitleRowBgColor(props)};
  border-bottom: ${() => `1px solid ${() => G.getTheme('tables.rows.borderColor')}`};
`;

TitleRow.defaultProps = {
  minHeight: 40,
};

export const TitleCell = styled.div`
  ${space}
  ${width}
  ${zIndex}
  ${fontSize}
  ${minWidth}

  height: 100%;
`;

TitleCell.defaultProps = {
  minWidth: 'unset',
};

export const StickedCell = styled.div`
  ${space}
  ${width}
  ${zIndex}

  position: sticky;

  top: ${({ top }) => top || 'unset'};
  left: ${({ left }) => left || 'unset'};
  right: ${({ right }) => right || 'unset'};
  bottom: ${({ bottom }) => bottom || 'unset'};
  min-width: ${({ minWidth }) => minWidth || 'unset'};
  background-color: ${(props) => props.bgColor || 'unset'};
`;

StickedCell.defaultProps = {
  zIndex: 1,
};

const getTableRowBgColor = (props, hover) => (
  R.or(
    props.bgColor,
    G.ifElse(
      props.active,
      G.getTheme(G.ifElse(
        props.mainColors,
        R.or(props.activeRowColor, 'tables.rows.bgColorActive'),
        'tables.rows.additionalBgColor',
      )),
      G.ifElse(
        hover,
        G.getTheme('tables.rows.hoverBgColor'),
        G.getTheme(G.ifElse(
          R.prop('highlighted', props),
          'tables.rows.highlightedBgColor',
          'tables.rows.bgColor',
        )),
      ),
    ),
  )
);

const getTableRowBorderColor = (props) => G.ifElse(
  props.active,
  G.getTheme(G.ifElse(
    G.isNotNil(props.activeBorderColor),
    props.activeBorderColor,
    G.ifElse(props.mainColors, 'tables.rows.borderColorActive', 'tables.rows.additionalBorderColor'),
  )),
  G.getTheme(G.ifElse(
    G.isNotNil(props.borderColor),
    props.borderColor,
    'tables.rows.borderColor',
  )),
);

export const TableCell = styled(Box)`
  ${zIndex}

  height: 100%;
`;

TableCell.defaultProps = {
  minWidth: 'unset',
};

export const CellWithBackground = styled.div`
  ${borderRadius}

  width: 100%;
  height: 100%;
  background-color: ${(props) => getTableRowBgColor(props)};
  border: ${(props) => `1px solid ${getTableRowBorderColor(props)}`};

  ${border}
`;

export const TableRow = styled(Flex)`
  min-width: 100%;
  width: fit-content;

  background-color: ${(props) => getTableRowBgColor(props)};
  text-decoration: ${({ textDecoration }) => textDecoration};
  border: ${(props) => `1px solid ${getTableRowBorderColor(props)}`};

  ${border}

  &:hover {
    background-color: ${(props) => getTableRowBgColor(props, true)};
    & .cell-with-background {
      background-color: ${(props) => getTableRowBgColor(props, true)};
    }
  }
`;

TableRow.defaultProps = {
  height: 40,
};

export const TitleCellWithBackground = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${(props) => getTitleRowBgColor(props)};
`;

export const WithBoxShadow = styled.div`
  height: 100%;
  display: flex;
  box-shadow: ${({ boxShadow }) => (
    boxShadow || `inset -2px 0px 0px ${G.getTheme('colors.boxShadowLightGrey')}`
  )};
`;

const getCheckboxBorderColor = (props) => {
  const { disabled, checkBoxCellBorderColor } = props;

  if (G.isTrue(disabled)) return G.getTheme('colors.light.lightGrey');

  if (G.isString(checkBoxCellBorderColor)) return checkBoxCellBorderColor;

  return G.getTheme('forms.checkboxes.borderColor');
};

export const Checkbox = styled.input`
  ${space}

  z-index: 0;
  position: relative;

  &:focus {
    outline: none;
  }

  &:before {
    left: 0;
    top: 50%;
    content: '';
    width: 16px;
    height: 16px;
    border-radius: 5px;
    position: absolute;
    transform: translate(0, -50%);
    background-color: ${() => G.getTheme('forms.checkboxes.bgColor')};
    border: 1px solid ${(props) => getCheckboxBorderColor(props)};
  }

  &:checked:before {
    content: '';
    width: 16px;
    height: 16px;
    border-radius: 5px;
    position: absolute;
    background-color: ${() => G.getTheme('forms.checkboxes.bgColorChecked')};
    border: 1px solid ${({ disabled, mainColors }) => G.getTheme(G.ifElse(
      disabled,
      'colors.light.lightGrey',
      G.ifElse(
        G.isTrue(mainColors),
        'forms.checkboxes.borderColorChecked',
        'forms.checkboxes.additionalBorderColor',
      ),
    ))};
  }

  &:checked:after {
    border: 1px solid ${(props) => G.getTheme(G.ifElse(
      props.mainColors,
      'forms.checkboxes.textColorChecked',
      'forms.checkboxes.additionalTextColor',
    ))};
    left: 0;
    top: 50%;
    width: 3px;
    height: 8px;
    content: '';
    position: absolute;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg) translate(0, -90%);
  }
`;

export const FlexContainer = styled.div`
  ${space}
  ${height}

  display: flex;
  align-items: ${({ align }) => align};
  justify-content: ${({ justify }) => justify};
  flex-direction: ${({ direction }) => direction};
`;

export const ActionIcon = styled(Box)`
  cursor: pointer;

  ${({ disabled }) => (
    R.and(
      disabled,
      `
        opacity: 0.5;
        cursor: not-allowed;
      `,
    )
  )}
`;
